import React, { createContext, useContext, useState, useEffect } from 'react';
import http from '../api/http';

const AuthContext = createContext(null);

const DEMO_ACCOUNTS = [
  {
    email: 'samuel.w@example.com',
    password: 'Admin123!',
    user: {
      id: 1,
      nom: 'Kouassi',
      prenom: 'Awa',
      email: 'samuel.w@example.com',
      role: 'super_admin',
      filiale_id: null,
      filiale: null,
      filiales: [], // Sera chargé dynamiquement depuis l'API
    },
  },
  {
    email: 'ivan.p@example.net',
    password: 'Admin123!',
    user: {
      id: 2,
      nom: 'Adjo',
      prenom: 'Jean',
      email: 'ivan.p@example.net',
      role: 'admin_pays',
      filiale_id: 1,
      filiale: null, // Sera chargé dynamiquement depuis l'API
      filiales: [], // Sera chargé dynamiquement depuis l'API
    },
  },
];

const normalizeUser = (raw) => {
  if (!raw) return null;
  const filiale = raw.filiale || (raw.pays
    ? {
        id: raw.pays_id || raw.pays.id,
        code: raw.pays.code,
        libelle: raw.pays.nom || raw.pays.libelle,
        pays: raw.pays.nom,
        devise: raw.pays.devise,
      }
    : null);
  return {
    ...raw,
    libprofil: raw.libprofil, // Garantir que libprofil est préservé
    filiale,
    filiales: raw.filiales || [], // Les filiales seront chargées dynamiquement depuis l'API
    filiale_id: raw.filiale_id || null,
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const fetchPermissions = async () => {
    try {
      const response = await http.get('/api/permissions/my-permissions');
      setPermissions(response.data);
    } catch (error) {
      console.error('Erreur récupération permissions:', error);
      setPermissions({});
    }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    if (token.startsWith('demo.')) {
      const demo = DEMO_ACCOUNTS.find((account) => `demo.${account.user.id}` === token);
      setUser(demo ? demo.user : null);
      if (!demo) localStorage.removeItem('token');
      setLoading(false);
      return;
    }

    try {
      const response = await http.get('/api/auth/verify');
      setUser(normalizeUser(response.data.user));
      await fetchPermissions();
    } catch (error) {
      localStorage.removeItem('token');
      setUser(null);
      setPermissions({});
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await http.post('/api/auth/login', { email: username, password });
      const { token, user: nextUser } = response.data;
      localStorage.setItem('token', token);
      setUser(normalizeUser(nextUser));
      await fetchPermissions();
      return { success: true };
    } catch (error) {
      const demo = DEMO_ACCOUNTS.find(
        (account) => account.email === username.trim().toLowerCase() && account.password === password
      );
      if (demo) {
        localStorage.setItem('token', `demo.${demo.user.id}`);
        setUser(demo.user);
        setPermissions({});
        return { success: true };
      }
      return {
        success: false,
        error: error.response?.data?.error || 'Erreur de connexion',
      };
    }
  };

  const logout = async () => {
    try {
      // Appeler la route backend pour logger la déconnexion
      await http.post('/api/auth/logout');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      // Continuer même si l'appel échoue
    } finally {
      localStorage.removeItem('token');
      sessionStorage.removeItem('rh_filiale_scope');
      setUser(null);
      setPermissions({});
    }
  };

  const forgotPassword = async (username) => {
    try {
      const response = await http.post('/api/auth/forgot-password', { username });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Impossible d\'envoyer le code de réinitialisation',
      };
    }
  };

  const resetPassword = async (code, newPassword) => {
    try {
      const response = await http.post('/api/auth/reset-password', { code, newPassword });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Impossible de réinitialiser le mot de passe',
      };
    }
  };

  const hasPermission = (permissionKey) => {
    return permissions[permissionKey] === true;
  };

  return (
    <AuthContext.Provider value={{ user, loading, permissions, login, logout, checkAuth, hasPermission, forgotPassword, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
